import { backendUrl } from "../services/backendUrl";

export function createSlug(value = "") {
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function makeAbsoluteUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const path = String(imagePath).trim();

  if (!path) {
    return "";
  }

  // Keep full external URLs unchanged
  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:")
  ) {
    return path;
  }

  // Legacy /website/-prefixed path — strip the prefix, already relative otherwise
  if (path.startsWith("/website/")) {
    return backendUrl(path.replace(/^\/website/, ""));
  }

  if (path.startsWith("public/uploads/")) {
    return backendUrl(`/${path.slice("public/".length)}`);
  }

  // Old static project paths:
  // /ARHouse/1.jpeg
  // /Clara Garden/1.jpeg
  // /MapleGarden.png
  if (path.startsWith("/")) {
    return backendUrl(path);
  }

  // Uploaded admin images saved as:
  // uploads/image.jpg
  if (path.startsWith("uploads/")) {
    return backendUrl(`/${path}`);
  }

  // Plain file names saved in uploads folder
  return backendUrl(`/uploads/${path}`);
}

function normalizeStatus(status = "") {
  const normalizedStatus = status.trim().toLowerCase();

  if (
    normalizedStatus === "completed" ||
    normalizedStatus === "completed & sold out" ||
    normalizedStatus === "sold" ||
    normalizedStatus === "sold out"
  ) {
    return "completed";
  }

  if (
    normalizedStatus === "in progress" ||
    normalizedStatus === "ongoing" ||
    normalizedStatus === "available" ||
    normalizedStatus === "ready to move" ||
    normalizedStatus === "ready to move in"
  ) {
    return "available";
  }

  return normalizedStatus || "available";
}

function getImagePath(image) {
  if (!image) {
    return "";
  }

  if (typeof image === "string") {
    return image;
  }

  return (
    image.image_url ||
    image.image_path ||
    image.url ||
    image.path ||
    ""
  );
}

export function mapApiProject(project = {}) {
  const coverImage =
    project.cover_image_url ||
    project.project_image_url ||
    project.project_image ||
    "";

  const projectImages =
    project.gallery ||
    project.images ||
    project.project_images ||
    [];

  const gallery = Array.isArray(projectImages)
    ? projectImages
        .map((image) => makeAbsoluteUrl(getImagePath(image)))
        .filter(Boolean)
    : [];

  const gallerySelectionSource =
    project.gallery_images ??
    project.galleryImages ??
    projectImages;

  const galleryImages = Array.isArray(gallerySelectionSource)
    ? gallerySelectionSource
        .map((image) => makeAbsoluteUrl(getImagePath(image)))
        .filter(Boolean)
    : [];

  const cover = makeAbsoluteUrl(coverImage);

  // Add cover image to gallery when gallery is empty
  const completeGallery =
    gallery.length > 0
      ? gallery
      : cover
        ? [cover]
        : [];

  const configuration =
    project.configuration ||
    project.config ||
    "";

  const projectSize =
    project.project_size ||
    project.size ||
    "";

  const projectType =
    project.project_type ||
    project.category ||
    "Residential";

  const projectName =
    project.project_name ||
    project.name ||
    "Untitled Project";

  const status =
    project.status ||
    "In Progress";

  return {
    id: project.id,

  slug:
  project.slug ||
  `${createSlug(projectName)}-${project.id}`,

    name: projectName,

    projectName,

    category: projectType,

    projectType,

    configuration,

    config: configuration,

    size: projectSize,

    projectSize,

    details: [configuration, projectSize]
      .filter(Boolean)
      .join(" • "),

    location:
      project.location ||
      "",

    locationShort:
      project.location_short ||
      project.locationShort ||
      project.location ||
      "",

    status,

    statusKey: normalizeStatus(status),

    groupKey:
      normalizeStatus(status) === "completed"
        ? "completed"
        : "ongoing",

    approval:
      project.approval ||
      project.approvals ||
      "",

    description:
      project.description ||
      project.overview ||
      "Project details will be updated soon.",

    overview:
      project.overview ||
      project.description ||
      "",

    cover,

    gallery: completeGallery,

    galleryImages,

    mapUrl:
      project.map_url ||
      project.mapUrl ||
      "",

    totalUnits:
      project.total_units ||
      project.totalUnits ||
      "",

    completionDate:
      project.completion_date ||
      project.completionDate ||
      "",

    highlights: Array.isArray(project.highlights)
      ? project.highlights
      : [],
  };
}
